/**
 * Talking to the content CMS.
 *
 * The password is sent on every admin call as a header and is held in
 * sessionStorage, never localStorage: it should not survive the tab being
 * closed, and it must never be written into the bundle or a .env the build
 * inlines. The server compares it — nothing here decides whether a write is
 * allowed, it only carries the answer.
 */
import CustomAxios from '../utils/CustomAxios';

const KEY = 'cms_admin_password';

export const getPassword = () => sessionStorage.getItem(KEY) || '';
export const setPassword = (value) => sessionStorage.setItem(KEY, value || '');
export const clearPassword = () => sessionStorage.removeItem(KEY);

const auth = () => ({ headers: { 'X-CMS-Password': getPassword() } });

/** Everything, drafts included. */
export const listPages = () =>
  CustomAxios.get('/cms/admin/pages/', auth()).then((r) => r.data?.pages || []);

/** The draft if there is one, otherwise the live copy. */
export const getPage = (slug) =>
  CustomAxios.get(`/cms/admin/pages/${slug}/`, auth()).then((r) => r.data);

/** Save as a draft. Publishing is deliberate and separate. */
export const savePage = (slug, page) =>
  CustomAxios.put(`/cms/admin/pages/${slug}/`, page, auth()).then((r) => r.data);

export const createPage = (page) =>
  CustomAxios.post('/cms/admin/pages/', page, auth()).then((r) => r.data);

export const publishPage = (slug) =>
  CustomAxios.post(`/cms/admin/pages/${slug}/publish/`, {}, auth()).then((r) => r.data);

export const deletePage = (slug) =>
  CustomAxios.delete(`/cms/admin/pages/${slug}/`, auth()).then((r) => r.data);

/** The published copy, exactly as the app will receive it. */
export const getPublished = (slug) =>
  CustomAxios.get(`/cms/pages/${slug}/`).then((r) => r.data);

/**
 * A message worth showing.
 *
 * The server answers a bad password with 401 and a missing configuration with
 * 503, and those need different reactions from whoever is reading — one is
 * "try again", the other is "call someone". Collapsing both into "failed"
 * sends an editor hunting for a typo that is not there.
 */
export function describeError(error) {
  const status = error?.response?.status;
  const detail = error?.response?.data?.error;
  if (status === 401) return 'That content password is not right.';
  if (status === 503) return detail || 'Content storage is not configured on the server.';
  if (status === 409) return detail || 'A page with that name already exists.';
  return detail || error?.message || 'Something went wrong.';
}
